import React, { useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import MenuNav from "../components/Menunav";
import MobileMenu from "../components/MobileMenu";
import { useFavoris } from "../hooks/useFavoris";
import { FiHeart } from "react-icons/fi";
import { FaGasPump, FaBatteryHalf, FaCar } from "react-icons/fa";
import { collection, getDocs, query, where } from "firebase/firestore";
import FeaturedVehiclesModal from "../components/FeaturedVehiclesModal";

export default function CataloguePage() {
  const [vehicles, setVehicles] = useState([]);
  const [filters, setFilters] = useState({
    recherche: "",
    marque: "",
    prixMax: "",
    carburant: "",
    transmission: "",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 992);

  const itemsPerPage = 24;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const favorisSlugs = useFavoris();

  useEffect(() => {
    const fetchVehicles = async () => {
      try {
        const ref = collection(db, "vehicules_auto");
        const q = query(ref, where("enVente", "==", true));
        const snap = await getDocs(q);
        const list = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setVehicles(list);
      } catch (error) {
        console.error("Erreur lors de la récupération des véhicules :", error);
      }
    };
    fetchVehicles();
  }, []);

  useEffect(() => {
    const term = searchParams.get("recherche") || "";
    if (term) {
      setFilters((prev) => ({ ...prev, recherche: term }));
    }
  }, [searchParams]);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 992);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ✅ Remonter en haut quand on change de page
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [currentPage]);

  const handleFilterChange = (e) => {
    setCurrentPage(1);
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleCarburantFilter = (type) => {
    setCurrentPage(1);
    setFilters((prev) => ({
      ...prev,
      carburant: prev.carburant === type ? "" : type,
    }));
  };

  const filteredVehicles = useMemo(() => {
    return vehicles.filter((v) => {
      const rechercheTerms = filters.recherche
        .trim()
        .toLowerCase()
        .split(" ")
        .filter(Boolean);

      const marqueModele = `${v.marque || ""} ${v.modele || ""}`.toLowerCase();

      const matchRecherche = rechercheTerms.every((term) =>
        marqueModele.includes(term)
      );

      const matchMarque =
        !filters.marque ||
        v.marque?.toLowerCase().includes(filters.marque.toLowerCase());

      const matchPrix =
        !filters.prixMax || parseFloat(v.prix) <= parseFloat(filters.prixMax);

      const matchCarburant =
        !filters.carburant ||
        v.ficheTechnique?.carburant === filters.carburant;

      const matchTransmission =
        !filters.transmission ||
        v.ficheTechnique?.transmission === filters.transmission;

      return (
        matchRecherche &&
        matchMarque &&
        matchPrix &&
        matchCarburant &&
        matchTransmission
      );
    });
  }, [vehicles, filters]);

  const totalPages = Math.ceil(filteredVehicles.length / itemsPerPage);

  const paginatedVehicles = filteredVehicles.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div className="container-fluid py-4 bg-white text-dark">
      <Helmet>
        <title>
          Catalogue de Voitures à Importer | Véhicules Neufs et d’Occasions –
          TheBridgeOfWorld.com
        </title>
        <meta
          name="description"
          content="Découvrez notre catalogue complet de voitures importées au Togo : SUV, berlines, 4x4, utilitaires, véhicules neufs ou d’occasions. Recherchez par marque, prix, carburant, année, et plus encore."
        />
        <meta
          name="keywords"
          content="The bridge, importation voiture Afrique, SUV occasions, voiture neuve Togo, voiture pas chère, véhicules Toyota, Nissan, Ford, Hyundai, catalogue voiture Afrique, auto importée, vente voiture Togo"
        />
        <meta name="author" content="The Bridge of World" />
        <meta
          property="og:title"
          content="Catalogue de Véhicules à Importer | TheBridgeOfWorld.com"
        />
        <meta
          property="og:description"
          content="Parcourez une large sélection de véhicules importés fiables. Trouvez le modèle parfait selon vos critères : prix, marque, année, carburant, et plus."
        />
        <meta
          property="og:url"
          content="https://thebridgeofworld.com/catalogue"
        />
        <meta property="og:type" content="website" />
      </Helmet>

      <MenuNav />
      <MobileMenu />

      <div className="row">
        <div className="col-12 mb-3 d-lg-none">
          <button
            className="btn btn-outline-primary w-100"
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? "Masquer les filtres" : "Afficher les filtres"}
          </button>
        </div>

        {(showFilters || isDesktop) && (
          <div className="col-lg-3 mb-4 position-sticky top-0">
            <div className="bg-light border p-3 rounded shadow-sm">
              <h5 className="mb-3">🔍 Filtres</h5>

              <input
                type="text"
                className="form-control mb-2"
                name="recherche"
                value={filters.recherche}
                onChange={handleFilterChange}
                placeholder="Marque ou Modèle..."
              />

              <div className="d-flex justify-content-between mb-3 gap-2">
                <button
                  type="button"
                  className={`btn btn-outline-primary w-50 ${
                    filters.carburant === "Essence" ? "active" : ""
                  }`}
                  onClick={() => handleCarburantFilter("Essence")}
                >
                  <FaGasPump className="me-2" />
                  Essence
                </button>

                <button
                  type="button"
                  className={`btn btn-outline-primary w-50 ${
                    filters.carburant === "Diesel" ? "active" : ""
                  }`}
                  onClick={() => handleCarburantFilter("Diesel")}
                >
                  <FaGasPump className="me-2" />
                  Diesel
                </button>
              </div>

              <div className="d-flex justify-content-between mb-3 gap-2">
                <button
                  type="button"
                  className={`btn btn-outline-primary w-50 ${
                    filters.carburant === "Électrique" ? "active" : ""
                  }`}
                  onClick={() => handleCarburantFilter("Électrique")}
                >
                  <FaBatteryHalf className="me-2" />
                  Electrique
                </button>

                <button
                  type="button"
                  className={`btn btn-outline-primary w-50 ${
                    filters.carburant === "Hybride" ? "active" : ""
                  }`}
                  onClick={() => handleCarburantFilter("Hybride")}
                >
                  <FaCar className="me-2" />
                  Hybride
                </button>
              </div>

              <input
                type="number"
                className="form-control mb-2"
                name="prixMax"
                value={filters.prixMax}
                onChange={handleFilterChange}
                placeholder="Prix max"
              />

              <select
                className="form-control mb-2"
                name="transmission"
                value={filters.transmission}
                onChange={handleFilterChange}
              >
                <option value="">Toutes transmissions</option>
                <option value="Manuelle">Manuelle</option>
                <option value="Automatique">Automatique</option>
              </select>
            </div>
          </div>
        )}

        <div className={`col-lg-${showFilters || isDesktop ? "9" : "12"}`}>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="fw-bold">🧾 {filteredVehicles.length} véhicules trouvés</h4>
          </div>

          <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 g-4">
            {paginatedVehicles.length === 0 && (
              <div className="col-12">
                <p className="text-muted">Aucun véhicule trouvé.</p>
              </div>
            )}

            {paginatedVehicles.map((v, i) => (
              <div key={`${v.id}-${i}`} className="col-md-6 col-lg-4">
                <div
                  className="position-relative rounded overflow-hidden shadow-sm vehicle-card"
                  style={{ cursor: "pointer" }}
                  onClick={() => v.slug && navigate(`/vehicules/${v.slug}`)}
                >
                  <button
                    type="button"
                    className="position-absolute top-0 end-0 m-2 bg-white border-0 rounded-circle shadow"
                    style={{
                      width: "38px",
                      height: "38px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 2,
                    }}
                    title={
                      favorisSlugs.includes(v.slug)
                        ? "Retirer des favoris"
                        : "Ajouter aux favoris"
                    }
                  >
                    <FiHeart
                      size={18}
                      color={favorisSlugs.includes(v.slug) ? "red" : "gray"}
                    />
                  </button>

                  <img
                    loading="lazy"
                    src={v.imageUrls?.[0] || "/assets/default.jpg"}
                    alt={`${v.marque} ${v.modele}`}
                    className="w-100"
                    style={{ height: "280px", objectFit: "cover" }}
                  />

                  <div className="d-block d-md-none position-absolute top-0 start-0 m-2 px-3 py-1 rounded bg-dark bg-opacity-75 text-white fw-semibold small shadow-sm">
                    {v.marque} {v.modele}
                  </div>

                  <div className="vehicle-overlay d-none d-md-flex flex-column justify-content-end p-3">
                    <div className="card-body">
                      <h5 className="card-title text-dark fw-semibold">
                        {v.marque} {v.modele}
                      </h5>
                      <p className="card-text small text-muted mb-1">
                        Année : {v.annee}
                      </p>
                      <p className="card-text small text-muted">
                        {v.ficheTechnique?.carburant} | {v.ficheTechnique?.transmission}
                      </p>
                    </div>

                    <div className="text-white">
                      <p className="h5 text-primary fw-bold mb-3" style={{color: "#0D1B41"}}>
                        {parseInt(v.prix, 10) > 0 ? (
                          <>
                            {parseInt(v.prix, 10).toLocaleString("fr-FR")}{" "}
                            <span
                              className="text-muted fw-semibold"
                              style={{ fontSize: "0.9rem" }}
                            >
                              FCFA
                            </span>
                          </>
                        ) : (
                          <span style={{ color: "#F7941E" }}>
                            Prix à la demande
                          </span>
                        )}
                      </p>
                      <button type="button" className="btn btn-bridge btn-sm">
                        Voir détails
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="d-flex justify-content-center mt-4">
              <ul className="pagination">
                {Array.from({ length: totalPages }, (_, i) => (
                  <li
                    key={i}
                    className={`page-item ${currentPage === i + 1 ? "active" : ""}`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(i + 1)}
                    >
                      {i + 1}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
  <FeaturedVehiclesModal />
    </div>
  );
}
